<template>
<!-- HISTORY: no-version
   V260604.1: Added download feature for rejects files.
   V260212.1: Added isPowerAdmin prop and a delete btn for the Power Admin + Displayed time alongside dates.
   V250804.1: Removed click event from the year directories.
   V250722.1: Introduced isExpanded prop to execute toggleAll() on demand + Modified expandAll() to toggleAll().
   V250625.1: Changed the name from FileTree to BtFolderTree.
   V250618.1: Made directories of the right panel clickable and raised the click event.
   V250617.1: 1st version/release.
-->
<v-list dense class="compact-list">
   <div v-for="(node) in nodes" :key="node.path">
      <template v-if="isDirectory(node)"><!-- DIRECTORY -->
         <v-list-item dense
            @click="toggle(node)"
            class="folder-item compact-item"
         >
            <v-icon small left>
               {{ node._expanded ? 'folder_open' : 'folder' }}
            </v-icon>
            <v-list-item-content dense class="py-0">
               <v-list-item-title dense class="font-weight-bold">
                  <span v-if="isClickable && node.name.length <= 2"
                     class="clickable-name"
                     @click.stop="onNodeClick(node)"
                  >{{ node.name }}
                     <!-- V260212 -->
                     <v-icon v-if="isPowerAdmin"
                        small
                        class="ml-2 delete-icon"
                        @click.stop="onDeleteClick(node)"
                     >delete</v-icon>
                  </span>
                  <span v-else>{{ node.name }}</span>
               </v-list-item-title>
            </v-list-item-content>
         </v-list-item>

         <!-- Children inside expand-transition -->
         <v-expand-transition>
            <div v-show="node._expanded" class="ml-3"><!-- ml-4 -->
               <!-- V260604: added @download -->
               <file-tree
                  :nodes="node.children"
                  :is-clickable="isClickable"
                  :parent-year="yearForChild(node)"
                  :parent-month="monthForChild(node)"
                  :is-power-admin="isPowerAdmin"
                  @click="$emit('click', $event)"
                  @delete="$emit('delete', $event)"
                  @download="$emit('download', $event)"
               />
            </div>
         </v-expand-transition>
      </template>
      <template v-else><!-- FILE -->
         <v-list-item class="compact-item">
            <v-icon small left>description</v-icon>
            <v-list-item-content dense class="py-0">
               <v-list-item-title>
                  {{ node.name }}
                  <!-- V260604 -->
                  <v-icon v-if="node.name.endsWith('_rejects.csv')"
                     small
                     class="ml-2 download-icon"
                     title="Download CSV"
                     @click.stop="onDownloadClick(node)"
                  >file_download</v-icon>
               </v-list-item-title>
               <v-list-item-subtitle><!-- class="text-caption" -->
                  {{ formatSize(node.size) }} • {{ formatDate(node.modified) }}
               </v-list-item-subtitle>
            </v-list-item-content>
         </v-list-item>
      </template>
   </div>
</v-list>
</template>

<script>
const NAME = "BtFolderTree";

export default {
   name: NAME,

   props: {
      nodes: {
         type: Array,
         required: true
      },
      isClickable: Boolean,
      parentYear: String,
      parentMonth: String,
      isExpanded: Boolean,
      isPowerAdmin: Boolean  //V260212
   },

   watch: {
      isExpanded: {
         immediate: false,
         handler(val) {
            this.toggleAll(this.nodes, val);
         }
      }
   },

   methods: {
      isDirectory(node) {
         return node.type === "directory";
      },

      toggle(node) {
         if (this.isDirectory(node)) {
            this.$set(node, "_expanded", !node._expanded);
         }
      },

      formatSize(bytes) {
         if (!bytes) return "";

         const sizes = ["B", "KB", "MB", "GB"];
         const i = Math.floor(Math.log(bytes) / Math.log(1024));
         return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
      },

      formatDate(isoString) {
         if (!isoString) return "";

         const date = new Date(isoString);
         //V260212: return date.toLocaleDateString();
         return date.toLocaleString();
      },

      toggleAll(nodes, isExpanded) {
         nodes.forEach(node => {
            if (this.isDirectory(node)) {
               this.$set(node, "_expanded", isExpanded);  //true
               if (node.children) this.toggleAll(node.children, isExpanded);
            }
         });
      },

      yearForChild(node) {
         return this.parentYear || (node.name.length === 4 ? node.name : null);
      },

      monthForChild(node) {
         return this.parentYear && node.name.length <= 2 ? node.name : null;
      },

      onNodeClick(node) {
         // console.warn(`in ${NAME}/onNodeClick(): node=${JSON.stringify(node)}`);
         this.$emit('click', {
            // node,
            year: this.parentYear || (node.name.length === 4 ? node.name : null),
            month: this.parentMonth || (node.name.length <= 2 ? node.name : null)
         });
      },

      // V260212
      onDeleteClick(node) {
         // alert(`onDeleteClick(): node=${JSON.stringify(node)}`);
         if (confirm(`Are you sure you want to permanently delete the ${this.parentYear}/${node.name} folder?`)) {
            this.$emit('delete', {
               year: this.parentYear,
               month: node.name
            });
         }
      },

      // V260604
      onDownloadClick(node) {
         this.$emit('download', {
            year: this.parentYear,
            month: this.parentMonth,
            node
         });
      }
   },

   components: {
      FileTree: null
   },

   created() {
      this.$options.components.FileTree = this.$options;
      this.toggleAll(this.nodes, true);
   }
};
</script>

<style scoped>
.compact-list {
   padding: 0;
}
.compact-item {
   min-height: 28px !important;
   padding-top: 2px !important;
   padding-bottom: 2px !important;
}
.folder-item {
   cursor: pointer;
   /* background-color: #f5f5f5; */
   padding: 0px;
}
.v-list-item__title,
.v-list-item__subtitle {
   line-height: 1.2rem !important;
   font-size: 0.75rem !important;
}
.ml-4 {
   margin-left: 16px;
}
.ml-3 {
   margin-left: 12px;
}
.v-list-item {
   cursor: pointer;
   padding: 0;
}
.clickable-name {
   cursor: pointer;
   color: #1976d2;
}
.clickable-name:hover {
   text-decoration: underline;
}
/* V260604 */
.download-icon {
   cursor: pointer;
   color: #1976d2;
}
.download-icon:hover {
   color: #1565c0;
}
</style>
